import { useState, useCallback } from 'react';
import type { Editor } from '@tiptap/react';
import { processEmailTemplateWithMarking, highlightUnresolvedMergeFields } from '@/utils/emailTemplateProcessor';
import { EmailTemplateSelectPopover } from './EmailTemplateSelectPopover';
import { EmailTemplateCreateModal } from './EmailTemplateCreateModal';
import { useEmailTemplates, type EmailTemplate } from '@/hooks/email/useEmailTemplates';

export interface TemplateContext {
  lead?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    companies?: { name?: string | null } | null;
  } | null;
  deal?: {
    value?: number | null;
    stageName?: string | null;
  } | null;
  currentUser?: {
    full_name?: string | null;
    email?: string | null;
  } | null;
}
import {
  Undo,
  Redo,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Image as ImageIcon,
  Table as TableIcon,
  Minus,
  Code,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Palette,
  Paperclip,
  Sparkles,
  X,
  MoreHorizontal,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface EmailToolbarProps {
  editor: Editor | null;
  onAttach?: () => void;
  /** @deprecated Use templateContext + onTemplateSelect instead. Kept for backwards compat. */
  onTemplates?: () => void;
  onAI?: () => void;
  /** Whether AI mode is currently active — highlights the IA button */
  aiModeActive?: boolean;
  attachmentCount?: number;
  compact?: boolean;
  /** Deal/lead/user context for auto-resolving merge fields on template apply */
  templateContext?: TemplateContext;
  /** Called when a template is applied — lets parent fill the subject field */
  onTemplateSelect?: (bodyHtml: string, subject?: string | null) => void;
}

function ToolbarButton({
  active,
  disabled,
  onClick,
  children,
  title,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'p-1 rounded transition-colors',
        disabled && 'opacity-40 cursor-not-allowed',
        active
          ? 'bg-primary/10 text-primary'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      {children}
    </button>
  );
}

function ToolbarSeparator() {
  return <div className="w-px h-4 bg-border mx-1" />;
}

// Link popover
function LinkPopover({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');

  const handleOpen = useCallback((isOpen: boolean) => {
    if (isOpen && editor.isActive('link')) {
      setUrl(editor.getAttributes('link').href || '');
    } else if (isOpen) {
      setUrl('');
    }
    setOpen(isOpen);
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!url) return;
    editor.chain().focus().setLink({ href: url }).run();
    setOpen(false);
    setUrl('');
  }, [editor, url]);

  const removeLink = useCallback(() => {
    editor.chain().focus().unsetLink().run();
    setOpen(false);
    setUrl('');
  }, [editor]);

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Link"
          className={cn(
            'p-1 rounded transition-colors',
            editor.isActive('link')
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <div className="space-y-2">
          <Input
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyLink()}
            className="h-8 text-sm"
            autoFocus
          />
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs" onClick={applyLink}>
              Aplicar
            </Button>
            {editor.isActive('link') && (
              <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={removeLink}>
                Remover link
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Image popover
function ImagePopover({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');

  const insertFromUrl = useCallback(() => {
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
    setOpen(false);
    setUrl('');
  }, [editor, url]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      editor.chain().focus().setImage({ src: base64 }).run();
      setOpen(false);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [editor]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Imagem"
          className="p-1 rounded transition-colors text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ImageIcon className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <div className="space-y-3">
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">URL da imagem</p>
            <div className="flex gap-2">
              <Input
                placeholder="https://..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && insertFromUrl()}
                className="h-8 text-sm"
              />
              <Button size="sm" className="h-8 text-xs" onClick={insertFromUrl}>
                Inserir
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Upload</p>
            <label className="flex items-center justify-center gap-2 h-8 px-3 rounded border border-dashed border-border text-xs text-muted-foreground hover:bg-muted cursor-pointer transition-colors">
              <ImageIcon className="h-3.5 w-3.5" />
              Escolher arquivo
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Table grid popover
function TablePopover({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const [hoverRows, setHoverRows] = useState(0);
  const [hoverCols, setHoverCols] = useState(0);

  const insertTable = useCallback((rows: number, cols: number) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    setOpen(false);
  }, [editor]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Tabela"
          className="p-1 rounded transition-colors text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <TableIcon className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <p className="text-xs text-muted-foreground mb-2">
          {hoverRows > 0 ? `${hoverRows} × ${hoverCols}` : 'Selecione o tamanho'}
        </p>
        <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {Array.from({ length: 4 }, (_, row) =>
            Array.from({ length: 4 }, (_, col) => (
              <button
                key={`${row}-${col}`}
                type="button"
                className={cn(
                  'w-6 h-6 rounded border transition-colors',
                  row < hoverRows && col < hoverCols
                    ? 'bg-primary/20 border-primary/40'
                    : 'bg-muted/50 border-border hover:bg-muted'
                )}
                onMouseEnter={() => { setHoverRows(row + 1); setHoverCols(col + 1); }}
                onMouseLeave={() => { setHoverRows(0); setHoverCols(0); }}
                onClick={() => insertTable(row + 1, col + 1)}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Color popover
const COLOR_PALETTE = [
  { label: 'Padrão', value: '' },
  { label: 'Primary', value: 'hsl(280, 100%, 15%)' },
  { label: 'Destructive', value: 'hsl(0, 84%, 60%)' },
  { label: 'Success', value: 'hsl(142, 71%, 35%)' },
  { label: 'Warning', value: 'hsl(38, 92%, 50%)' },
  { label: 'Foreground', value: 'hsl(0, 0%, 10%)' },
  { label: 'Muted', value: 'hsl(0, 0%, 45%)' },
  { label: 'Blue', value: 'hsl(217, 91%, 60%)' },
  { label: 'Indigo', value: 'hsl(239, 84%, 67%)' },
  { label: 'Teal', value: 'hsl(172, 66%, 40%)' },
  { label: 'Pink', value: 'hsl(330, 81%, 60%)' },
  { label: 'Orange', value: 'hsl(25, 95%, 53%)' },
];

function ColorPopover({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);

  const setColor = useCallback((color: string) => {
    if (color) {
      editor.chain().focus().setColor(color).run();
    } else {
      editor.chain().focus().unsetColor().run();
    }
    setOpen(false);
  }, [editor]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Cor do texto"
          className="p-1 rounded transition-colors text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Palette className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="grid grid-cols-6 gap-1.5">
          {COLOR_PALETTE.map((c) => (
            <button
              key={c.label}
              type="button"
              title={c.label}
              className={cn(
                'w-6 h-6 rounded-full border border-border transition-transform hover:scale-110',
                !c.value && 'flex items-center justify-center'
              )}
              style={c.value ? { backgroundColor: c.value } : { background: 'linear-gradient(135deg, #ddd 50%, #fff 50%)' }}
              onClick={() => setColor(c.value)}
            >
              {!c.value && <X className="h-3 w-3 text-muted-foreground" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Overflow menu for rarely-used toolbar items (Table, HR, Code)
function OverflowMenu({ editor }: { editor: Editor }) {
  const [tableOpen, setTableOpen] = useState(false);
  const [hoverRows, setHoverRows] = useState(0);
  const [hoverCols, setHoverCols] = useState(0);

  const insertTable = (rows: number, cols: number) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    setTableOpen(false);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Mais opções de formatação"
          aria-label="Mais opções de formatação"
          className="p-1 rounded transition-colors text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[160px]">
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTableOpen((v) => !v); }}>
          <TableIcon className="h-3.5 w-3.5 mr-2" /> Tabela
        </DropdownMenuItem>
        {tableOpen && (
          <div className="px-2 py-1.5">
            <p className="text-xs text-muted-foreground mb-1.5">
              {hoverRows > 0 ? `${hoverRows} × ${hoverCols}` : 'Selecione o tamanho'}
            </p>
            <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
              {Array.from({ length: 4 }, (_, row) =>
                Array.from({ length: 4 }, (_, col) => (
                  <button
                    key={`${row}-${col}`}
                    type="button"
                    className={cn(
                      'w-5 h-5 rounded border transition-colors',
                      row < hoverRows && col < hoverCols
                        ? 'bg-primary/20 border-primary/40'
                        : 'bg-muted/50 border-border hover:bg-muted'
                    )}
                    onMouseEnter={() => { setHoverRows(row + 1); setHoverCols(col + 1); }}
                    onMouseLeave={() => { setHoverRows(0); setHoverCols(0); }}
                    onClick={() => insertTable(row + 1, col + 1)}
                  />
                ))
              )}
            </div>
          </div>
        )}
        <DropdownMenuItem onSelect={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus className="h-3.5 w-3.5 mr-2" /> Linha horizontal
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => editor.chain().focus().toggleCodeBlock().run()}>
          <Code className="h-3.5 w-3.5 mr-2" /> Bloco de código
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function EmailToolbar({
  editor,
  onAttach,
  onTemplates,
  onAI,
  aiModeActive = false,
  attachmentCount = 0,
  compact = false,
  templateContext,
  onTemplateSelect,
}: EmailToolbarProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { incrementUsage } = useEmailTemplates();

  const handleApplyTemplate = useCallback((template: EmailTemplate) => {
    if (!editor) return;
    const resolved = processEmailTemplateWithMarking(
      template.body_html,
      templateContext?.lead ?? undefined,
      templateContext?.currentUser
        ? { full_name: templateContext.currentUser.full_name, email: templateContext.currentUser.email }
        : undefined,
      templateContext?.deal ?? undefined,
      true,
    );
    const finalHtml = highlightUnresolvedMergeFields(resolved);
    editor.commands.setContent(finalHtml, { emitUpdate: false });
    onTemplateSelect?.(finalHtml, template.subject);
    incrementUsage(template.id);
  }, [editor, templateContext, onTemplateSelect, incrementUsage]);

  if (!editor) return null;

  return (
  <>
    <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-border bg-muted/30 rounded-t-lg">
      {/* Group 1 — History */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Desfazer"
      >
        <Undo className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Refazer"
      >
        <Redo className="h-3.5 w-3.5" />
      </ToolbarButton>

      <ToolbarSeparator />

      {/* Group 2 — Inline formatting */}
      <ToolbarButton
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Negrito"
      >
        <Bold className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Itálico"
      >
        <Italic className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        title="Sublinhado"
      >
        <UnderlineIcon className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        title="Tachado"
      >
        <Strikethrough className="h-3.5 w-3.5" />
      </ToolbarButton>

      <ToolbarSeparator />

      {/* Group 3 — Blocks */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title="Título"
            className={cn(
              'p-1 rounded transition-colors',
              editor.isActive('heading')
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <Heading className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[120px]">
          <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
            <span className="text-lg font-bold">H1</span>
            <span className="ml-2 text-xs text-muted-foreground">Título 1</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
            <span className="text-base font-bold">H2</span>
            <span className="ml-2 text-xs text-muted-foreground">Título 2</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
            <span className="text-sm font-bold">H3</span>
            <span className="ml-2 text-xs text-muted-foreground">Título 3</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => editor.chain().focus().setParagraph().run()}>
            <span className="text-sm">P</span>
            <span className="ml-2 text-xs text-muted-foreground">Parágrafo</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ToolbarButton
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Lista com marcadores"
      >
        <List className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Lista numerada"
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        title="Citação"
      >
        <Quote className="h-3.5 w-3.5" />
      </ToolbarButton>

      <ToolbarSeparator />

      {/* Group 4 — Insert */}
      <LinkPopover editor={editor} />
      <ImagePopover editor={editor} />
      <OverflowMenu editor={editor} />

      <ToolbarSeparator />

      {/* Group 5 — Alignment */}
      <ToolbarButton
        active={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        title="Alinhar à esquerda"
      >
        <AlignLeft className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        title="Centralizar"
      >
        <AlignCenter className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        title="Alinhar à direita"
      >
        <AlignRight className="h-3.5 w-3.5" />
      </ToolbarButton>

      <ToolbarSeparator />

      {/* Group 6 — Color */}
      <ColorPopover editor={editor} />

      {/* Spacer */}
      <div className="flex-1" />
      <div className="w-px h-5 bg-border mx-1" />

      {/* Group 7 — Special actions */}
      {onAttach && (
        <button
          type="button"
          onClick={onAttach}
          className="flex items-center gap-1 px-1.5 py-1 rounded text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          title="Anexar arquivo"
        >
          <Paperclip className="h-3.5 w-3.5" />
          {!compact && <span>Anexar</span>}
          {attachmentCount > 0 && (
            <span className="ml-0.5 px-1.5 py-0.5 text-xs bg-primary/20 text-primary rounded-full">
              {attachmentCount}
            </span>
          )}
        </button>
      )}
      <EmailTemplateSelectPopover
        onSelectTemplate={handleApplyTemplate}
        onCreateNew={() => setShowCreateModal(true)}
      />
      {onAI && (
        <button
          type="button"
          onClick={onAI}
          className={cn(
            'flex items-center gap-1 px-1.5 py-1 rounded text-xs transition-colors',
            aiModeActive
              ? 'bg-primary/10 text-primary hover:bg-primary/20'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
          title="Composição IA"
          aria-label="Composição IA"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {!compact && <span>IA</span>}
        </button>
      )}
    </div>

    <EmailTemplateCreateModal
      open={showCreateModal}
      onOpenChange={setShowCreateModal}
    />
  </>
  );
}
